/** Wraps a search bar and result list */

import { Link as AddLink } from '@tunasong/icons'
import { Button, TypeFilterChips } from '@tunasong/ui-lib'
import { EntityFilters, isEditablePage, toEntityLink } from '@tunasong/models'
import type { SearchSummary } from '@tunasong/models'
import type { BlockCommand } from '@tunasong/plugin-block/ui'
import { runEditorCommand, useEditor } from '@tunasong/plugin-lib'
import type { Entity, EntityType, Persisted, SearchFilter } from '@tunasong/schemas'
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { MouseEvent } from 'react'
import { allQueries } from './queries/index.js'
import { useOtherSpacesCount } from './other-spaces-count.js'
import styles from './search-bar.module.css'
import { scopeSpace } from './scope-space.js'
import { SearchBar } from './search-bar.js'
import { useAdvancedSearch } from './search.hook.js'
import { useUserConfig, useParentChain } from '@tunasong/ui-lib'
import { parseQuerySegments } from './lib/parse-segments.js'

/** Recent, with nothing typed: the last 8 opened (#2555 M5) */
const recentSize = 8

/** The quick chips with nothing typed (#2555 M5): songs, setlists, bands and people */
const quickTypes: EntityType[] = ['song', 'setlist', 'bandspace', 'profile']

export interface FullSearchProps {
  initialQuery?: string
  currentEntity?: Persisted<Entity>
  selected?: SearchSummary
  size?: number
  defaultFilter?: SearchFilter
  showRecentOnEmptyQuery?: boolean
  /** Show the option to search within the current entity */
  showSearchWithinSpace?: boolean
  onOpen(result: SearchSummary): void
  /** Cmd or Ctrl+Enter, or a Cmd or Ctrl+click (#2555) */
  onOpenInNewTab?(result: SearchSummary): void
  /** On a phone, the header's Cancel (#2555, screen F) */
  onCancel?(): void
  onClose(): void
}
export const FullSearch = ({
  onOpen,
  onOpenInNewTab,
  onCancel,
  currentEntity,
  initialQuery = '',
  defaultFilter,
  showRecentOnEmptyQuery = true,
  showSearchWithinSpace: showSearchWithinSpace = true,
  selected,
  size = 25,
  /** The dialog closes itself: Esc on an empty query, the backdrop, or Cancel on a phone */
  onClose: _onClose,
}: FullSearchProps) => {
  const [query, setQuery] = useState<string>(initialQuery)
  /** Opened inside a space, search starts there; the scope control widens it (#2555) */
  const [scoped, setScoped] = useState<boolean>(true)

  const [filter, setFilter] = useState<SearchFilter | undefined>(defaultFilter)
  /** The query without the filter.  */
  const [cleanQuery, setCleanQuery] = useState<string | undefined>(undefined)
  /**
   * Type chips (GH #2519 B): session-only, and a new query resets them. A selected chip narrows the search
   * on the server, exactly as a typed `type:` segment does, so every page of results is narrowed.
   */
  const [chipTypes, setChipTypes] = useState<EntityType[]>([])
  /** The types the query found before any chip narrowed it, so the other chips stay offered */
  const [foundTypes, setFoundTypes] = useState<EntityType[]>([])
  const handleQuery = (query: string) => {
    setQuery(query)
    const { filter, cleanQuery } = parseQuerySegments(query)
    setCleanQuery(cleanQuery)
    setFilter(filter)
    setChipTypes([])
  }

  const [userConfig] = useUserConfig()

  const queryString = cleanQuery ?? query

  const parentChain = useParentChain(currentEntity)

  /** Opened inside a space, search offers its scope; an Unfiled song has none (#2555) */
  const space = scopeSpace(currentEntity, parentChain)
  const showScope = showSearchWithinSpace && Boolean(currentEntity) && Boolean(space)
  const spaceIdFilter = showScope && scoped && space ? space.id : undefined

  /**
   * With nothing typed the omnibox shows the recent list — so it must SAY `recent`. It
   * used to build the recent DSL and still announce `queryType: 'default'`. The deleted
   * DSL sniffer answered on the body shape and did not care; a host that dispatches on
   * the query type alone answered the wrong question, and the dialog opened empty.
   */
  /** Typed text or a filter, or a quick chip with nothing typed (#2555 M5) */
  const isTyped = Boolean(queryString || filter)
  const isTextQuery = isTyped || chipTypes.length > 0
  const queryType = isTextQuery ? 'default' : 'recent'

  const searchFilter: SearchFilter | undefined =
    chipTypes.length > 0 ? { ...filter, types: [...(filter?.types ?? []), ...chipTypes] } : filter

  const querySpec = isTextQuery
    ? allQueries.default({ query: queryString, size, filter: searchFilter, parentId: spaceIdFilter })
    : showRecentOnEmptyQuery
      ? allQueries.recent({ parentId: spaceIdFilter, size: recentSize })
      : undefined

  /**
   * What the omnibox MEANT. The DSL above expresses it for OpenSearch; a local kernel
   * cannot execute that, so the same values travel as query parameters beside it.
   */
  const intent = isTextQuery
    ? { query: queryString, size, parentId: spaceIdFilter, types: searchFilter?.types, tags: searchFilter?.tags }
    : { size: recentSize, parentId: spaceIdFilter }

  const {
    results,
    searching = false,
    total,
    getNext,
    isError,
    retry,
  } = useAdvancedSearch(querySpec, {
    resultFilter: EntityFilters.defaultSearch,
    size,
    debounceDelay: 200,
    queryType,
    pollingInterval: 10_000,
    intent,
  })

  /** While scoped, the list's last line counts the matches elsewhere: one count-only request per settled query */
  const otherSpacesCount = useOtherSpacesCount({
    enabled: Boolean(spaceIdFilter) && isTextQuery,
    query: queryString,
    filter: searchFilter,
    scopedTotal: total,
  })

  /**
   * The earlier results stay while a new search runs (#2555 M4): no empty list, and no "Nothing matches" flash, between
   * one answer and the next.
   */
  const resultsKey = results.map(result => result.id).join(',')
  const [lastResults, setLastResults] = useState({ key: resultsKey, results })
  /** Kept during render, keyed by ids, so a fresh empty array from the hook can't loop (React's previous-render pattern) */
  if (!searching && resultsKey !== lastResults.key) {
    setLastResults({ key: resultsKey, results })
  }
  const shown = searching && results.length === 0 ? lastResults.results : results

  /** Until a chip narrows it, each answer says which types the query found */
  const resultTypes = [...new Set(results.map(result => result.type))].sort()
  const resultTypesKey = resultTypes.join(',')
  useEffect(() => {
    if (chipTypes.length === 0) {
      setFoundTypes(resultTypesKey === '' ? [] : (resultTypesKey.split(',') as EntityType[]))
    }
  }, [chipTypes.length, resultTypesKey])

  const editor = useEditor({ allowNull: true })

  const handleOpen = useCallback(
    (result: SearchSummary) => {
      onOpen(result)
    },
    [onOpen]
  )

  /** Insert a link needs an editable page and its editor (the button and Alt+Enter alike) */
  const isEditable = useMemo(() => isEditablePage(currentEntity), [currentEntity]) && Boolean(editor)

  const plugins = editor?.meta.pluginList
  const insertLink = (entity: Persisted<Entity>) => {
    const { id } = entity
    if (!(editor && id && plugins)) {
      return
    }
    const entityLink = toEntityLink(entity)
    editor.insertNode(entityLink)

    void runEditorCommand<BlockCommand>({
      commandId: 'block-insert-break',
      editor,
      plugins,
      globalContext: {} as never,
      userConfig,
    })
  }
  const addLink = (entity: Persisted<Entity>) => (ev: MouseEvent) => {
    ev.stopPropagation()
    insertLink(entity)
  }

  /** A plain function: the React Compiler memoizes it, and it depends on `addLink`, which changes with the editor */
  const renderActions = (entity: Persisted<Entity>) => {
    if (!(isEditable && entity)) {
      return null
    }
    return (
      <Button
        onClick={addLink(entity)}
        aria-label={`Insert a link to ${entity.name ?? 'Untitled'}`}
        aria-keyshortcuts="Alt+Enter"
      >
        <AddLink />
      </Button>
    )
  }

  /** The quick type chips with nothing typed (#2555 M5): the same chips, for the types people look for */
  const quickChips = (
    <TypeFilterChips
      entities={quickTypes.map(type => ({ type }))}
      value={chipTypes}
      showCounts={false}
      onChange={setChipTypes}
    />
  )
  /**
   * A new account has no Recent yet (screen E): the line, then the chips, in place of the list. Recent is scoped, so an
   * empty Recent inside a space says nothing about the account; there the chips show alone.
   */
  const isNewAccount = !isTextQuery && !searching && shown.length === 0 && !isError && !spaceIdFilter

  const emptyContent = isTyped ? (
    spaceIdFilter && space ? (
      <div className={styles.emptyState}>
        <p>
          Nothing matches “{queryString}” in {space.name || 'Untitled'}.
        </p>
        <Button variant="contained" onClick={() => setScoped(false)}>
          Search everywhere
        </Button>
      </div>
    ) : (
      <div className={styles.emptyState}>
        <p>Nothing matches “{queryString}”.</p>
        <p className={styles.emptyHint}>Check the spelling, or try fewer words.</p>
      </div>
    )
  ) : isNewAccount ? (
    <div className={styles.emptyState}>
      <p className={styles.emptyHint}>Find anything you’ve written or your bands have shared.</p>
      {quickChips}
    </div>
  ) : null

  return (
    <SearchBar
      loading={searching}
      query={query}
      // A pre-filled query (Link song's guess, GH #2547) is selected, so typing replaces it
      selectQueryOnFocus={Boolean(initialQuery) && query === initialQuery}
      selected={selected}
      entities={shown}
      total={isTextQuery ? total : undefined}
      filter={filter}
      listLabel={isTextQuery ? 'Results' : 'Recent'}
      heading={isTextQuery ? undefined : 'Recent'}
      emptyContent={emptyContent}
      error={isError ? { onRetry: retry } : undefined}
      aboveResults={
        isTyped && foundTypes.length > 0 ? (
          <TypeFilterChips
            entities={foundTypes.map(type => ({ type }))}
            value={chipTypes}
            showCounts={false}
            onChange={setChipTypes}
          />
        ) : isTyped || isNewAccount ? null : (
          quickChips
        )
      }
      scope={showScope && space ? { space: space.name || 'Untitled', scoped, onChange: setScoped } : undefined}
      otherSpacesCount={otherSpacesCount}
      onCancel={onCancel}
      onQuery={handleQuery}
      onChange={handleOpen}
      onOpenInNewTab={onOpenInNewTab}
      onAltEnter={isEditable ? insertLink : undefined}
      onEndOfResults={getNext}
      renderHotkeys={renderActions}
    />
  )
}
